/**
 * The template set: complete compositions, as data.
 *
 * A template is not a colour preset. It is a whole *look* — the geometry that is
 * drawn on the design, where the words sit inside it, where a picture goes when the
 * template is built around one, and the typography and colours it starts from —
 * and it is written here as plain data so that adding one is adding an object rather
 * than editing a renderer (`components/Post/PostDesignRenderer` draws it).
 *
 * Three rules keep a template from becoming a decision nobody can change:
 *
 *  - **The geometry is a `d` string in a 0–100 box.** Paths are authored once, at a
 *    fixed scale, and the renderer fits that box to whatever canvas the design is
 *    read on — a phone, a tablet, a desktop browser — so one template is one
 *    composition at every width and no template has to know the size it will be
 *    drawn at.
 *  - **A colour a path can take from its author says so** (`PATH_FROM_AUTHOR`)
 *    instead of naming a literal. The toolbar then offers that colour, and
 *    `templatePathColour` resolves it — a template with no such path simply has
 *    nothing to offer, rather than a colour control that does nothing.
 *  - **A template leaves its background to the author.** Everything it declares is a
 *    *starting* value (`applyStyledTemplate` writes it over the current one), so a
 *    template can always be re-coloured, re-typed and re-slotted afterwards.
 *
 * The colours are literal and deliberately not read from the app's theme: a designed
 * post is a picture of a sentence, and it must look the same on a phone in dark mode
 * as on one in light, for the same reason a photograph does.
 */
import {type StyledPost} from '#/state/postStyle/types'

/** The colours the background and ink controls offer, in the order they are shown. */
export const STYLE_PALETTE: readonly string[] = [
  '#111111',
  '#F4EFE3',
  '#9FD8A8',
  '#0C3B2E',
  '#F2B33D',
  '#D9542B',
  '#8E5AA8',
  '#2B4C7E',
  '#F5D0E0',
  '#120C1E',
  '#EBD9A8',
  '#26200F',
]

/**
 * The wash the author's own selection is drawn with, while they are editing.
 *
 * Not part of a mark and never seen by a reader: it is the editor's own affordance, and
 * it is drawn *by the design*, on the design's words and at the design's size, because a
 * selection painted by the input underneath would be a block of the input's line boxes —
 * over words it cannot even see, since the input's own text is invisible. Translucent so
 * the words stay readable through it.
 */
export const DESIGN_SELECTION_WASH = 'rgba(0, 133, 255, 0.32)'

/** The colours the highlight control paints with. */
export const HIGHLIGHT_PALETTE: readonly string[] = [
  '#43C25F',
  '#F2B33D',
  '#2B4C7E',
  '#D9542B',
  '#8E5AA8',
  '#111111',
]

/**
 * What an untouched design draws.
 *
 * A design that has been turned on but not yet coloured is still a design, so it is
 * given something an author would have chosen: warm paper with dark ink, the weight a
 * title is set in, and the words centred — the same shape every template below
 * starts from.
 */
export const DEFAULT_STYLE: Omit<StyledPost, 'text'> = {
  v: 1,
  background: {type: 'solid', color: '#F4EFE3'},
  ink: '#1A1A1A',
  weight: 'bold',
  align: 'center',
  highlightColor: HIGHLIGHT_PALETTE[0],
}

/** The ornaments a template may place on a design, as the glyph they are drawn with. */
export const STYLE_STICKERS: Record<string, string> = {
  crescent: '☾',
  spotlight: '✦',
  quotemark: '❞',
}

/**
 * The mark a path's colour carries when it belongs to the author.
 *
 * A sentinel rather than a colour, because the alternative is a template that
 * *looks* editable and is not: a path written with a literal is the template's own
 * decision, and a path written with this is the author's to make.
 */
export const PATH_FROM_AUTHOR = 'template'

/** A template's geometry, in a 0–100 box. */
export type TemplatePath = {
  /** SVG path data. Authored at one scale and fitted to the canvas by the renderer. */
  d: string
  fill?: string
  stroke?: string
  /** In the same 0–100 units as `d`; the renderer scales it with the box. */
  strokeWidth?: number
  opacity?: number
}

/** Where a picture goes when the template is built around one. */
export type TemplateImageSlot = 'none' | 'full' | 'band-top' | 'band-bottom'

/** How a template arranges itself around its words. */
export type TemplateLayout = {
  /** Where the words hang when the design is shorter than the canvas. */
  vertical: 'center' | 'top' | 'bottom'
  imageSlot: TemplateImageSlot
  /** The picture's share of the canvas when it is a band, 0–1. */
  imageShare?: number
  /** Extra whitespace above and below the words, as a share of the canvas width. */
  padTop?: number
  padBottom?: number
}

export type StyledTemplate = {
  id: string
  /** True when the template's paths take the author's colour (`PATH_FROM_AUTHOR`). */
  pathsEditable: boolean
  layout: TemplateLayout
  paths: readonly TemplatePath[]
  /** The ornament the template places, when it places one. */
  sticker?: string
  /** The state the template writes onto the design. */
  style: Omit<StyledPost, 'text'>
}

/**
 * The templates the toolbar offers.
 *
 * Five, because five is what a row can show at once and what these designs are: a
 * night of celebration, a title card, a quotation on paper, a portrait with the
 * picture above the words, and a full-bleed picture with the words on it. The first
 * three are compositions of colour and geometry; the last two are built around a
 * picture the author brings.
 */
export const STYLE_TEMPLATES: readonly StyledTemplate[] = [
  {
    id: 'ramadan',
    pathsEditable: true,
    layout: {vertical: 'center', imageSlot: 'none'},
    sticker: 'crescent',
    paths: [
      {d: 'M 10 30 Q 10 10 30 10', stroke: PATH_FROM_AUTHOR, strokeWidth: 2},
      {d: 'M 90 70 Q 90 90 70 90', stroke: PATH_FROM_AUTHOR, strokeWidth: 2},
      /* A crescent, drawn as two arcs so it survives being stretched to a canvas. */
      {
        d: 'M 50 6 A 11 11 0 1 0 50 28 A 8 8 0 1 1 50 6 Z',
        fill: '#F2B33D',
        opacity: 0.9,
      },
    ],
    style: {
      v: 1,
      background: {type: 'solid', color: '#0C3B2E'},
      ink: '#F2D98A',
      highlightColor: '#F2B33D',
      texture: true,
      weight: 'bold',
      align: 'center',
    },
  },
  {
    id: 'cinema',
    pathsEditable: false,
    layout: {vertical: 'bottom', imageSlot: 'none', padBottom: 0.08},
    sticker: 'spotlight',
    paths: [
      {d: 'M 50 -10 L 92 110 L 8 110 Z', fill: '#FFFFFF', opacity: 0.05},
      {d: 'M 50 -10 L 70 110 L 30 110 Z', fill: '#FFFFFF', opacity: 0.06},
      {d: 'M 12 84 L 88 84', stroke: '#F5D0E0', strokeWidth: 1, opacity: 0.5},
    ],
    style: {
      v: 1,
      background: {type: 'solid', color: '#120C1E'},
      ink: '#F5D0E0',
      highlightColor: '#8E5AA8',
      texture: true,
      weight: 'black',
      align: 'center',
    },
  },
  {
    id: 'quote',
    pathsEditable: true,
    layout: {vertical: 'center', imageSlot: 'none'},
    sticker: 'quotemark',
    paths: [
      {d: 'M 16 18 L 84 18', stroke: PATH_FROM_AUTHOR, strokeWidth: 1.5},
      {d: 'M 16 82 L 84 82', stroke: PATH_FROM_AUTHOR, strokeWidth: 1.5},
    ],
    style: {
      v: 1,
      background: {type: 'solid', color: '#EBD9A8'},
      ink: '#26200F',
      highlightColor: '#D9542B',
      texture: false,
      weight: 'bold',
      align: 'center',
    },
  },
  {
    id: 'portrait',
    pathsEditable: true,
    layout: {
      vertical: 'bottom',
      imageSlot: 'band-top',
      imageShare: 0.58,
      padTop: 0.02,
      padBottom: 0.06,
    },
    paths: [{d: 'M 22 0 L 78 0', stroke: PATH_FROM_AUTHOR, strokeWidth: 1.5}],
    style: {
      v: 1,
      background: {type: 'solid', color: '#111111'},
      ink: '#F7F1E6',
      highlightColor: '#F2B33D',
      texture: true,
      weight: 'bold',
      align: 'center',
    },
  },
  {
    id: 'editorial',
    pathsEditable: true,
    layout: {vertical: 'center', imageSlot: 'full'},
    paths: [
      {d: 'M 0 0 H 100 V 100 H 0 Z', fill: '#000000', opacity: 0.42},
      {
        d: 'M 82 14 Q 94 14 94 26',
        stroke: PATH_FROM_AUTHOR,
        strokeWidth: 1.5,
        opacity: 0.9,
      },
    ],
    style: {
      v: 1,
      background: {type: 'solid', color: '#120C1E'},
      ink: '#FFFFFF',
      highlightColor: '#43C25F',
      texture: true,
      weight: 'black',
      align: 'center',
    },
  },
]

/** A template by id, or `null` for one this build does not know. */
export function styledTemplate(id: string): StyledTemplate | null {
  return STYLE_TEMPLATES.find(template => template.id === id) ?? null
}

/**
 * A design from the defaults, for the moment the design mode is first opened.
 *
 * Kept here rather than in the toolbar so the composer, the renderer and the tests
 * all start from one answer rather than three that look alike.
 */
export function defaultStyledPost(text: string): StyledPost {
  return {...DEFAULT_STYLE, text}
}

/**
 * A template applied to a design — the author's words kept, the look replaced.
 *
 * The words and their ranges are untouched: a template is a decision about the
 * *composition*, not about the text, and re-choosing one is how an author changes
 * their mind without losing a highlight they painted by hand. A highlight whose
 * colour was the default takes the template's own colour; one the author chose keeps
 * it, and a path colour the author chose is kept for as long as the new template has
 * a path that takes one.
 */
export function applyStyledTemplate(
  style: StyledPost,
  template: StyledTemplate,
): StyledPost {
  const keptHighlightColor =
    style.highlightColor === DEFAULT_STYLE.highlightColor
      ? undefined
      : style.highlightColor
  const highlights = style.highlights?.map(entry =>
    entry.color === DEFAULT_STYLE.highlightColor &&
    template.style.highlightColor
      ? {...entry, color: template.style.highlightColor}
      : entry,
  )
  const templateValues = template.pathsEditable
    ? style.templateValues
    : undefined

  return {
    ...style,
    ...template.style,
    highlightColor: keptHighlightColor ?? template.style.highlightColor,
    highlights,
    template: template.id,
    templateValues,
    sticker: template.sticker,
  }
}

/**
 * What a template's paths are drawn in, where a path asked for the author's colour.
 *
 * The author's own choice first, then the template's ink, then the design's — so a
 * path is never the hole in a composition that a missing value would leave.
 */
export function templateAuthorPathColour(
  template: StyledTemplate | null,
  style: StyledPost,
): string {
  return (
    style.templateValues?.pathColor ??
    template?.style.ink ??
    style.ink ??
    DEFAULT_STYLE.ink ??
    '#1A1A1A'
  )
}

/** Where a template's words sit, as a flex alignment. */
export function templateVerticalAlign(
  template: StyledTemplate | null,
): 'flex-start' | 'center' | 'flex-end' {
  switch (template?.layout.vertical) {
    case 'top':
      return 'flex-start'
    case 'bottom':
      return 'flex-end'
    default:
      return 'center'
  }
}

/**
 * The template's own numbers, in pixels, for a canvas this wide.
 *
 * The only place a template's proportions are turned into a layout, and the reason a
 * picture band and the words under it cannot disagree about where the seam is: both
 * the band and the slot come out of this one call.
 */
export function templateLayoutPx(
  template: StyledTemplate | null,
  width: number,
): {
  padTop: number
  padBottom: number
  imageBand: number
  imageAboveText: boolean
} {
  const slot = template?.layout.imageSlot ?? 'none'
  const share = template?.layout.imageShare ?? 0.6
  /*
   * A band is measured from the *width*, never from the canvas's own height: the
   * canvas grows with the words on it, so a band that read its height would move
   * every time a line was typed — and the words would chase the picture down the
   * card. Width does not depend on the text, so one template is one composition.
   */
  const band = Math.round(
    width * (slot === 'band-bottom' ? share : share * 1.6),
  )
  return {
    padTop: Math.round((template?.layout.padTop ?? 0) * width),
    padBottom: Math.round((template?.layout.padBottom ?? 0) * width),
    imageBand: slot === 'band-top' || slot === 'band-bottom' ? band : 0,
    imageAboveText: slot === 'band-top' || slot === 'full',
  }
}
